import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconClose } from "../control/ui/icons";

/** How long a toast offers its Undo — well inside the server's 2-minute token. */
export const UNDO_TOAST_MS = 8000;

/**
 * A one-line result with an Undo (bulk select, issue #12): "Moved 3 clips to X · 1
 * already there · Undo". Portalled and fixed above everything but dialogs, dismissed by
 * its ✕, by time, or by the Undo itself. Without `onUndo` it is just the message —
 * how a refusal or "nothing changed" is said in the same place.
 *
 * The timer starts at mount and only then: give each toast its own `key` so a new result
 * gets a fresh eight seconds, and a re-render of the same one does not.
 */
export function UndoToast({
  message,
  onUndo,
  onDismiss,
}: {
  message: ReactNode;
  onUndo?: () => void;
  onDismiss: () => void;
}) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    const t = setTimeout(() => dismiss.current(), UNDO_TOAST_MS);
    return () => clearTimeout(t);
  }, []);

  return createPortal(
    <div
      role="status"
      className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-[55] flex w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] py-2.5 pl-4 pr-2 shadow-2xl"
    >
      <p className="min-w-0 flex-1 text-sm text-zinc-100">{message}</p>
      {onUndo ? (
        <button
          type="button"
          onClick={onUndo}
          className="shrink-0 rounded-md px-2.5 py-1 text-sm font-semibold text-[var(--color-accent-soft)] hover:bg-[var(--color-panel-2)]"
        >
          Undo
        </button>
      ) : null}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-white"
      >
        <IconClose width={14} height={14} />
      </button>
    </div>,
    document.body,
  );
}
